import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Avatar,
  Stack,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
  Divider,
  LinearProgress,
  Tab,
  Tabs,
  Badge,
  Fade,
  Zoom,
  Button
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  PictureAsPdf as PdfIcon,
  TableChart as TableIcon,
  Dashboard as DashboardIcon,
  Analytics as AnalyticsIcon,
  Download as DownloadIcon,
  Groups as GroupsIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  EventAvailable as EventAvailableIcon,
  BarChart as BarChartIcon,
  Insights as InsightsIcon,
  Rocket as RocketIcon,
  Palette as PaletteIcon,
  Speed as SpeedIcon,
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Terrain as TerrainIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useTranslation } from "react-i18next";


const MetricCard = ({ value, label, icon: Icon, color, trend, loading, sx }) => {
  const theme = useTheme();


  const isThemeColor = color && theme.palette[color];


  const cardBackground = sx?.backgroundColor
    ? sx.backgroundColor
    : isThemeColor
      ? `linear-gradient(135deg, ${alpha(theme.palette[color].main, 0.9)} 0%, ${alpha(theme.palette[color].dark, 0.9)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`;

  const cardColor = sx?.color || 'white';
  const shadowColor = sx?.backgroundColor
    ? alpha(sx.backgroundColor, 0.3)
    : isThemeColor
      ? alpha(theme.palette[color].main, 0.3)
      : alpha(theme.palette.primary.main, 0.3);

  return (
    <Zoom in={!loading} style={{ transitionDelay: loading ? '0ms' : '200ms' }}>
      <Card
        sx={{
          height: '100%',
          background: cardBackground,
          color: cardColor,
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 3,
          boxShadow: `0 8px 32px ${shadowColor}`,
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: `0 16px 48px ${shadowColor}`,
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -20,
            right: -20,
            width: 120,
            height: 120,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 80,
            height: 80,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
          },
          ...sx
        }}
      >
        <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h2" fontWeight="bold" sx={{ mb: 1, fontSize: { xs: '2.5rem', md: '3rem' } }}>
                {loading ? '--' : value}
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 500, mb: 1 }}>
                {label}
              </Typography>
              {trend && (
                <Chip
                  icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                  label={trend}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    backdropFilter: 'blur(10px)'
                  }}
                />
              )}
            </Box>
            <Avatar
              sx={{
                width: 70,
                height: 70,
                backgroundColor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <Icon sx={{ fontSize: 32 }} />
            </Avatar>
          </Stack>
        </CardContent>
        {loading && (
          <LinearProgress
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: 'rgba(255,255,255,0.3)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: 'white'
              }
            }}
          />
        )}
      </Card>
    </Zoom>
  );
};



const ActivityFeed = ({ activities, translateAction, translateActivityInfo, loading }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const isGeologyAction = (action) => {
    if (!action) return false;
    const act = String(action).toLowerCase();
    return act.includes('geolog') || act.includes('geologia') || act.includes('geología');
  };

  const getActionColor = (action) => {
    if (isGeologyAction(action)) {
      return '#8d6e63';
    }
    const colors = {
      'login': theme.palette.success.main,
      'logout': theme.palette.error.main,
      'upload': theme.palette.info.main,
      'pdf-count': theme.palette.primary.main,
      'check-file': theme.palette.warning.main,
      'download-pdf': theme.palette.warning.dark,
      'query-ocr-results': theme.palette.secondary.main,
      'view-table': theme.palette.grey[600],
      'navego a Dashboard': theme.palette.primary.dark,
      'navego a KPI': theme.palette.info.dark,
      'update-ocr': theme.palette.success.dark,
      'view-demo-fields': theme.palette.info.main,
      'update-table': theme.palette.success.main,
      'geology_wells_viewed': '#8d6e63',
      'Ingreso a gestión de geología': '#8d6e63',
      'Consulta rangos de geología': '#8d6e63',
      'Genera análisis de geología': '#8d6e63',
    };
    return colors[action] || theme.palette.primary.main;
  };

  const getActionIcon = (action) => {
    const iconStyle = { fontSize: 18 };
    if (isGeologyAction(action)) {
      return <TerrainIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
    }
    switch (action) {
      case 'login': return <LoginIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'logout': return <LogoutIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'upload': return <DownloadIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'pdf-count': return <PdfIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'check-file': return <PdfIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'download-pdf': return <DownloadIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'query-ocr-results': return <AnalyticsIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'view-table': return <TableIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'navego a Dashboard': return <DashboardIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'navego a KPI': return <AnalyticsIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'update-ocr': return <AnalyticsIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'view-demo-fields': return <TableIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'update-table': return <TableIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      case 'geology_wells_viewed':
      case 'Ingreso a gestión de geología':
      case 'Consulta rangos de geología':
      case 'Genera análisis de geología':
        return <TerrainIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      default: return <ScheduleIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
    }
  };

  return (
    <Fade in={!loading} timeout={500}>
      <Card sx={{
        height: '100%',
        borderRadius: 3,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
      }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="700" gutterBottom sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: theme.palette.primary.main
          }}>
            <RocketIcon /> {t('adminPanel.recentActivity')}
          </Typography>
          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {activities.slice(0, 8).map((activity, index) => (
              <React.Fragment key={activity.id}>
                <ListItem sx={{
                  px: 0,
                  py: 1.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 2
                  }
                }}>
                  <ListItemIcon sx={{ minWidth: 44 }}>
                    <Avatar sx={{
                      width: 32,
                      height: 32,
                      bgcolor: alpha(getActionColor(activity.action), 0.1),
                      '& .MuiSvgIcon-root': { fontSize: 16 }
                    }}>
                      {getActionIcon(activity.action)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box>
                        <Typography variant="subtitle2" fontWeight="600" color="primary" sx={{ mb: 0.5 }}>
                          {activity.userName}
                        </Typography>
                        <Typography variant="body2" fontWeight="500" sx={{ lineHeight: 1.4 }}>
                          {translateActivityInfo(activity.info)}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                        <Chip
                          label={translateAction(activity.action)}
                          size="small"
                          variant="filled"
                          sx={{
                            height: 22,
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            backgroundColor: alpha(getActionColor(activity.action), 0.1),
                            color: getActionColor(activity.action),
                            border: 'none'
                          }}
                        />
                        <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                          {activity.date} • {activity.hora}
                        </Typography>
                      </Stack>
                    }
                  />
                </ListItem>
                {index < activities.length - 1 && index < 7 && (
                  <Divider variant="inset" component="li" sx={{ my: 0.5 }} />
                )}
              </React.Fragment>
            ))}
          </List>
          {activities.length === 0 && !loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <InsightsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
              <Typography color="textSecondary" variant="body2">
                {t('adminPanel.noActivities')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Fade>
  );
};


const TraceabilityTab = ({
  logs,
  loading,
  expandedUser,
  handleUserClick,
  getActionIcon,
  getActionColor,
  getUserInitials,
  getTimeAgo,
  theme,
  translateAction,
  translateActivityInfo
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));


  const [userActivitiesLimit, setUserActivitiesLimit] = useState({});


  const loadMoreActivities = (userId) => {
    setUserActivitiesLimit(prev => ({
      ...prev,
      [userId]: (prev[userId] || 20) + 20
    }));
  };


  const loadLessActivities = (userId) => {
    setUserActivitiesLimit(prev => ({
      ...prev,
      [userId]: Math.max(20, (prev[userId] || 20) - 20)
    }));
  };


  const getLimitedUserActivities = (user) => {
    const limit = userActivitiesLimit[user.userId] || 20;
    return user.logs.slice(0, limit);
  };


  const hasMoreActivities = (user) => {
    const currentLimit = userActivitiesLimit[user.userId] || 20;
    return user.logs.length > currentLimit;
  };


  const hasLessActivities = (user) => {
    const currentLimit = userActivitiesLimit[user.userId] || 20;
    return currentLimit > 20;
  };

  const calculateActivityMetrics = () => {

    if (!logs || logs.length === 0) {
      return {
        last24h: 0,
        lastWeek: 0,
        engagement: 0,
        stats: {
          activitiesLast24h: 0,
          activeUsersLastWeek: 0,
          highlyActiveUsers: 0,
          totalUsers: 0,
          engagementRate: 0
        }
      };
    }

    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - (24 * 60 * 60 * 1000));
    const oneWeekAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));


    const activitiesLast24h = logs.flatMap(user => user.logs)
      .filter(log => new Date(log.timestamp) >= twentyFourHoursAgo).length;


    const activeUsersLastWeek = logs.filter(user =>
      user.lastActivity && new Date(user.lastActivity) >= oneWeekAgo
    ).length;


    const highlyActiveUsers = logs.filter(user => {
      const recentActivities = user.logs.filter(log =>
        new Date(log.timestamp) >= oneWeekAgo
      ).length;
      return recentActivities >= 3;
    }).length;

    const totalUsers = logs.length;

    const last24hPercentage = Math.min((activitiesLast24h / Math.max(totalUsers * 5, 20)) * 100, 100);
    const lastWeekPercentage = Math.min((activeUsersLastWeek / totalUsers) * 100, 100);
    const engagementPercentage = Math.min((highlyActiveUsers / totalUsers) * 100, 100);


    return {
      last24h: Math.round(last24hPercentage),
      lastWeek: Math.round(lastWeekPercentage),
      engagement: Math.round(engagementPercentage),
      stats: {
        activitiesLast24h,
        activeUsersLastWeek,
        highlyActiveUsers,
        totalUsers,
        engagementRate: engagementPercentage
      }
    };
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        <Box sx={{ flex: 2 }}>
          <ActivityFeed
            activities={logs.flatMap(user => user.logs)
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
              .slice(0, 50)}
            translateAction={translateAction}
            translateActivityInfo={translateActivityInfo}
            loading={loading}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Fade in={!loading} timeout={800}>
            <Card sx={{
              borderRadius: 3,
              background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
              border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
              height: '100%'
            }}>
              <CardContent>
                <Typography variant="h6" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: theme.palette.primary.main }}>
                  <InsightsIcon /> {t('adminPanel.activitySummary')}
                </Typography>

                {(() => {
                  const metrics = calculateActivityMetrics();
                  return (
                    <Stack spacing={2}>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            {t('adminPanel.activityLast24h')}
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color="info.main">
                            {metrics.stats?.activitiesLast24h || 0} {t('adminPanel.activities')}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.last24h}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.info.main, 0.2),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: theme.palette.info.main,
                              borderRadius: 4
                            }
                          }}
                        />
                      </Box>

                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            {t('adminPanel.activeUsersThisWeek')}
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color="success.main">
                            {metrics.stats?.activeUsersLastWeek || 0} {t('adminPanel.users')}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.lastWeek}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.success.main, 0.2),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: theme.palette.success.main,
                              borderRadius: 4
                            }
                          }}
                        />
                      </Box>

                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            {t('adminPanel.engagementRate')}
                          </Typography>
                          <Typography variant="body2" fontWeight="600" color="warning.main">
                            {metrics.stats?.engagementRate ? metrics.stats.engagementRate.toFixed(1) : 0}%
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.engagement}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.warning.main, 0.2),
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: theme.palette.warning.main,
                              borderRadius: 4
                            }
                          }}
                        />
                      </Box>
                    </Stack>
                  );
                })()}

                <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                  <Stack direction="row" spacing={3} justifyContent="space-around">
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="700" color="primary.main">
                        {logs.length}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('adminPanel.totalUsers')}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" fontWeight="700" color="secondary.main">
                        {logs.reduce((total, user) => total + user.logs.length, 0)}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {t('adminPanel.totalActivities')}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Box>
      </Stack>

      <Fade in={!loading} timeout={600}>
        <Card sx={{
          borderRadius: 3,
          background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{
              borderBottom: 1,
              borderColor: 'divider',
              p: 3,
              background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 100%)`
            }}>
              <Typography variant="h6" fontWeight="700" sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: theme.palette.primary.main
              }}>
                <GroupsIcon /> {t('adminPanel.userManagement')}
              </Typography>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
                <Stack alignItems="center" spacing={2}>
                  <CircularProgress size={60} />
                  <Typography color="textSecondary">{t('adminPanel.loading')}</Typography>
                </Stack>
              </Box>
            ) : (
              <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                {logs.map((user, index) => (
                  <Accordion
                    key={user.userId}
                    expanded={expandedUser === user.userId}
                    onChange={() => handleUserClick(user.userId)}
                    sx={{
                      '&:before': { display: 'none' },
                      border: 'none',
                      boxShadow: 'none',
                      background: 'transparent',
                      '&:not(:last-child)': {
                        borderBottom: '1px solid',
                        borderColor: alpha(theme.palette.divider, 0.3)
                      },
                      '&.Mui-expanded': {
                        margin: 0,
                        background: alpha(theme.palette.primary.main, 0.02)
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        px: 3,
                        py: 2.5,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.04),
                          borderRadius: 1
                        },
                        '&.Mui-expanded': {
                          minHeight: 56,
                          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                        <Box>
                          <Badge
                            color="success"
                            variant="dot"
                            invisible={
                              !user.lastActivity ||
                              (Date.now() - new Date(user.lastActivity).getTime()) > 24 * 60 * 60 * 1000
                            }
                            anchorOrigin={{
                              vertical: 'bottom',
                              horizontal: 'right',
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 52,
                                height: 52,
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                fontWeight: 'bold',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                              }}
                            >
                              {getUserInitials(user.userName)}
                            </Avatar>
                          </Badge>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="h6" component="div" fontWeight="600" noWrap>
                            {user.userName}
                          </Typography>
                          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'start', sm: 'center' }} sx={{ mt: 0.5 }}>
                            <Typography variant="body2" color="textSecondary">
                              {t('adminPanel.lastActivity')}: {getTimeAgo(user.lastActivity)}
                            </Typography>
                            <Chip
                              label={`${user.logs.length} ${t('adminPanel.actions')}`}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main
                              }}
                            />
                          </Stack>
                        </Box>

                        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                            {Object.entries(user.actionCounts).slice(0, 3).map(([action, count]) => (
                              <Tooltip key={action} title={`${count} ${translateAction(action)}`}>
                                <Chip
                                  icon={getActionIcon(action)}
                                  label={count}
                                  size="small"
                                  variant="filled"
                                  sx={{
                                    bgcolor: alpha(getActionColor(action), 0.1),
                                    '& .MuiChip-icon': { color: getActionColor(action) },
                                    mb: 0.5,
                                    fontWeight: 600
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Stack>
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={{ p: 0, bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                      <List dense sx={{ py: 1 }}>
                        {getLimitedUserActivities(user).map((log, logIndex) => (
                          <ListItem
                            key={log.id}
                            sx={{
                              px: 3,
                              py: 1.5,
                              borderBottom: '1px solid',
                              borderColor: alpha(theme.palette.divider, 0.2),
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.03)
                              }
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 44 }}>
                              <Avatar sx={{
                                width: 32,
                                height: 32,
                                bgcolor: alpha(getActionColor(log.action), 0.1),
                                '& .MuiSvgIcon-root': { fontSize: 16 }
                              }}>
                                {getActionIcon(log.action)}
                              </Avatar>
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                                  <Chip
                                    label={translateAction(log.action)}
                                    size="small"
                                    variant="filled"
                                    sx={{
                                      fontWeight: 600,
                                      bgcolor: alpha(getActionColor(log.action), 0.1),
                                      color: getActionColor(log.action),
                                      fontSize: '0.7rem'
                                    }}
                                  />
                                  <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace' }}>
                                    {log.date} {log.hora}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Typography variant="body2" sx={{ lineHeight: 1.4, color: 'text.primary' }}>
                                  {translateActivityInfo(log.info)}
                                </Typography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>

                      {(hasMoreActivities(user) || hasLessActivities(user)) && (
                        <Box sx={{ p: 2, display: 'flex', gap: 1, justifyContent: 'center', borderTop: `1px solid ${alpha(theme.palette.divider, 0.2)}` }}>
                          {hasLessActivities(user) && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => loadLessActivities(user.userId)}
                              startIcon={<ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />}
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                borderColor: theme.palette.primary.main,
                                color: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.1)
                                }
                              }}
                            >
                              {t('adminPanel.showLess')}
                            </Button>
                          )}

                          {hasMoreActivities(user) && (
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => loadMoreActivities(user.userId)}
                              endIcon={<ExpandMoreIcon />}
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                backgroundColor: theme.palette.primary.main,
                                '&:hover': {
                                  backgroundColor: theme.palette.primary.dark
                                }
                              }}
                            >
                              {t('adminPanel.showMore')}
                            </Button>
                          )}
                        </Box>
                      )}

                      {user.logs.length === 0 && (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                          <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                          <Typography color="textSecondary" variant="h6">
                            {t('adminPanel.noActivities')}
                          </Typography>
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                ))}

                {logs.length === 0 && (
                  <Paper sx={{ p: 6, textAlign: 'center', m: 2, borderRadius: 3 }}>
                    <Avatar sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      bgcolor: alpha(theme.palette.grey[300], 0.5)
                    }}>
                      <PersonIcon sx={{ fontSize: 40, color: 'grey.600' }} />
                    </Avatar>
                    <Typography variant="h5" color="textSecondary" gutterBottom>
                      {t('adminPanel.noUsers')}
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                      {t('adminPanel.noUsersDescription')}
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      </Fade>
    </Stack>
  );
};


const MembersTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Fade in timeout={500}>
      <Card sx={{
        borderRadius: 3,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        <CardContent sx={{ p: 6 }}>
          <Avatar sx={{
            width: 100,
            height: 100,
            mx: 'auto',
            mb: 4,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main
          }}>
            <PeopleIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="700" gutterBottom sx={{ color: theme.palette.primary.main }}>
            {t('adminPanel.membersTitle')}
          </Typography>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
            {t('adminPanel.membersDescription')}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <Chip
              icon={<RocketIcon />}
              label={t('adminPanel.comingSoon')}
              sx={{
                fontWeight: 600,
                px: 2,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main
              }}
              variant="outlined"
            />
            <Chip
              icon={<PaletteIcon />}
              label="Diseño en progreso"
              sx={{
                fontWeight: 600,
                px: 2,
                borderColor: theme.palette.secondary.main,
                color: theme.palette.secondary.main
              }}
              variant="outlined"
            />
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );
};

const GeneralTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Fade in timeout={500}>
      <Card sx={{
        borderRadius: 3,
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        textAlign: 'center'
      }}>
        <CardContent sx={{ p: 6 }}>
          <Avatar sx={{
            width: 100,
            height: 100,
            mx: 'auto',
            mb: 4,
            bgcolor: alpha(theme.palette.info.main, 0.1),
            color: theme.palette.info.main
          }}>
            <DashboardIcon sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="700" gutterBottom sx={{ color: theme.palette.info.main }}>
            {t('adminPanel.generalTitle')}
          </Typography>
          <Typography variant="h6" color="textSecondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
            {t('adminPanel.generalDescription')}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
            <Chip
              icon={<SpeedIcon />}
              label={t('adminPanel.inDevelopment')}
              sx={{
                fontWeight: 600,
                px: 2,
                borderColor: theme.palette.info.main,
                color: theme.palette.info.main
              }}
              variant="outlined"
            />
            <Chip
              icon={<TrendingUpIcon />}
              label="Próximamente"
              sx={{
                fontWeight: 600,
                px: 2,
                borderColor: theme.palette.warning.main,
                color: theme.palette.warning.main
              }}
              variant="outlined"
            />
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );
};

export default function AdminLogsPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalActivities: 0,
    activeToday: 0,
    activeThisWeek: 0
  });

  const theme = useTheme();
  const { t } = useTranslation();


  const translateAction = (action) => {
    return t(`adminPanel.actionTypes.${action}`, { defaultValue: action });
  };


  const translateTemplateKey = (key) => {
    return t(`adminPanel.templateKeys.${key}`, { defaultValue: key });
  };


  const translateCommonTerm = (term) => {
    return t(`adminPanel.commonTerms.${term}`, { defaultValue: term });
  };


  const translateActivityInfo = (info) => {
    if (typeof info !== 'string') {
      try { return JSON.stringify(info); } catch { return String(info); }
    }

    const patterns = [
      { regex: /^Login exitoso$/, template: "Login exitoso" },
      { regex: /^Cerró sesión$/, template: "Cerró sesión" },
      { regex: /^Subió archivo:\s*(.+)$/, template: "Subió archivo: {{fileName}}", extract: (match) => ({ fileName: match[1] }) },
      { regex: /^Consultó cantidad de PDFs$/, template: "Consultó cantidad de PDFs" },
      { regex: /^Consultó cantidad de PDFs:\s*(\d+)$/, template: "Consultó cantidad de PDFs: {{count}}", extract: (match) => ({ count: match[1] }) },
      { regex: /^Verificó existencia del archivo:\s*(.+)\s*\(exists:\s*(true|false)\)$/, template: "Verificó existencia del archivo: {{fileName}} (exists: {{exists}})", extract: (match) => ({ fileName: match[1], exists: translateCommonTerm(match[2]) }) },
      { regex: /^Visualizó el PDF:\s*(.+)$/, template: "Visualizó el PDF: {{fileName}}", extract: (match) => ({ fileName: match[1] }) },
      { regex: /^Solicitó URL de descarga para PDF:\s*(.+)$/, template: "Solicitó URL de descarga para PDF: {{fileName}}", extract: (match) => ({ fileName: match[1] }) },
      { regex: /^Consultó OCR DB y obtuvo documentos$/, template: "Consultó OCR DB y obtuvo documentos" },
      { regex: /^Consultó OCR DB y obtuvo\s*(\d+)\s*documentos$/, template: "Consultó OCR DB y obtuvo {{count}} documentos", extract: (match) => ({ count: match[1] }) },
      { regex: /^Consultó la base de datos OCR y navego a Dashboard\.$/, template: "Consultó la base de datos OCR y navego a Dashboard." },
      { regex: /^Consultó la base de datos OCR y navego a KPI\.$/, template: "Consultó la base de datos OCR y navego a KPI." },
      { regex: /^Actualizó campos del OCR ID:\s*(.+)$/, template: "Actualizó campos del OCR ID: {{id}}", extract: (match) => ({ id: match[1] }) },
      { regex: /^Consultó tabla con templateKey:\s*(.+)$/, template: "Consultó tabla con templateKey: {{templateKey}}", extract: (match) => ({ templateKey: translateTemplateKey(match[1]) }) },
      { regex: /^Tabla con templateKey:\s*(.+)\s*guardada$/, template: "Tabla con templateKey: {{templateKey}} guardada", extract: (match) => ({ templateKey: translateTemplateKey(match[1]) }) },
      { regex: /^Consultó\s*(\d+)\s*registros demo$/, template: "Consultó {{count}} registros demo", extract: (match) => ({ count: match[1] }) },
      { regex: /^Actualizó tabla ID:\s*(.+)$/, template: "Actualizó tabla ID: {{id}}", extract: (match) => ({ id: match[1] }) },
      { regex: /^Generó análisis geológico ID:\s*(.+)$/, template: "Generó análisis geológico ID: {{analysisId}}", extract: (match) => ({ analysisId: match[1] }) },
      { regex: /^Visualizó rangos del pozo:\s*(.+)$/, template: "Visualizó rangos del pozo: {{pozo}}", extract: (match) => ({ pozo: match[1] }) },
      { regex: /^Consultó análisis geológico pozo:\s*(.+)$/, template: "Consultó análisis geológico pozo: {{pozo}}", extract: (match) => ({ pozo: match[1] }) },
      { regex: /^Generó análisis geológico pozo:\s*(.+),\s*profundidad de consulta:\s*([^,]+),\s*refresh:\s*(true|false)$/, template: "Generó análisis geológico pozo: {{pozo}}, profundidad de consulta: {{depth}}, refresh: {{refresh}}", extract: (match) => ({ pozo: match[1], depth: match[2], refresh: match[3] }) },
      { regex: /^Generó análisis geológico pozo:\s*(.+),\s*depth:\s*([^,]+),\s*refresh:\s*(true|false)$/, template: "Generó análisis geológico pozo: {{pozo}}, profundidad de consulta: {{depth}}, refresh: {{refresh}}", extract: (match) => ({ pozo: match[1], depth: match[2], refresh: match[3] }) },
      { regex: /^Consultó partes pozo:\s*(.+),\s*profundidad de consulta:\s*([^,]+)$/, template: "Consultó partes pozo: {{pozo}}, profundidad de consulta: {{depth}}", extract: (match) => ({ pozo: match[1], depth: match[2] }) },
      { regex: /^Consultó partes pozo:\s*(.+),\s*depth:\s*([^,]+)$/, template: "Consultó partes pozo: {{pozo}}, profundidad de consulta: {{depth}}", extract: (match) => ({ pozo: match[1], depth: match[2] }) },
      { regex: /^Consultó pozos geología:\s*count=\s*(\d+)$/, template: "Consultó pozos geología: count={{count}}", extract: (match) => ({ count: match[1] }) }

    ];

    for (const pattern of patterns) {
      const match = info.match(pattern.regex);
      if (match) {
        if (pattern.extract) {
          const variables = pattern.extract(match);
          return t(`adminPanel.activityMessages.${pattern.template}`, variables);
        } else {
          return t(`adminPanel.activityMessages.${pattern.template}`);
        }
      }
    }

    return info;
  };


  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/logs`, {
        withCredentials: true,
      });


      const userLogs = {};
      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);


      const formatDateToDDMMYYYY = (dateString) => {
        if (!dateString) return '';


        const [year, month, day] = dateString.split('-');

        return `${day}/${month}/${year}`;
      };

      res.data.forEach((entry) => {
        const userId = entry.userId?._id || entry.userId;
        const userName = entry.userName || `Usuario ${userId}`;

        if (!userLogs[userId]) {
          userLogs[userId] = {
            userId,
            userName,
            logs: [],
            lastActivity: null,
            actionCounts: {},
            activityTimeline: []
          };
        }

        const details = entry.details || {};
        Object.entries(details).forEach(([date, events]) => {
          events.forEach((e) => {
            const timestamp = e.createdAt ? new Date(e.createdAt.$date || e.createdAt) : new Date(`${date} ${e.hora}`);


            const formattedDate = formatDateToDDMMYYYY(date);

            const logEntry = {
              id: `${entry._id}-${date}-${e.hora}`,
              date: formattedDate,
              hora: e.hora,
              info: e.info,
              action: e.action,
              timestamp,
              userName: userName,
            };

            userLogs[userId].logs.push(logEntry);


            userLogs[userId].actionCounts[e.action] =
              (userLogs[userId].actionCounts[e.action] || 0) + 1;


            if (!userLogs[userId].lastActivity || timestamp > userLogs[userId].lastActivity) {
              userLogs[userId].lastActivity = timestamp;
            }


            if (timestamp >= oneWeekAgo) {
              userLogs[userId].activityTimeline.push(timestamp);
            }
          });
        });
      });



      const today = new Date().toDateString();
      const activeTodayCount = Object.values(userLogs).filter(user =>
        user.lastActivity && new Date(user.lastActivity).toDateString() === today
      ).length;

      const activeThisWeekCount = Object.values(userLogs).filter(user =>
        user.lastActivity && user.lastActivity >= oneWeekAgo
      ).length;

      setStats({
        totalUsers: Object.keys(userLogs).length,
        totalActivities: res.data.reduce((sum, entry) => {
          const details = entry.details || {};
          return sum + Object.values(details).reduce((eventSum, events) => eventSum + events.length, 0);
        }, 0),
        activeToday: activeTodayCount,
        activeThisWeek: activeThisWeekCount
      });


      const sortedUsers = Object.values(userLogs).sort((a, b) =>
        new Date(b.lastActivity) - new Date(a.lastActivity)
      );


      sortedUsers.forEach(user => {
        user.logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      });

      setLogs(sortedUsers);
      setLoading(false);
    } catch (err) {
      console.error("Error al obtener logs:", err);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const isGeologyAction = (action) => {
    if (!action) return false;
    const act = String(action).toLowerCase();
    return act.includes('geolog') || act.includes('geologia') || act.includes('geología');
  };

  const getActionIcon = (action) => {
    const iconStyle = { fontSize: 20 };
    if (isGeologyAction(action)) {
      return <TerrainIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
    }
    switch (action) {
      case 'login': return <LoginIcon sx={{ ...iconStyle, color: theme.palette.success.main }} />;
      case 'logout': return <LogoutIcon sx={{ ...iconStyle, color: theme.palette.error.main }} />;
      case 'upload': return <DownloadIcon sx={{ ...iconStyle, color: theme.palette.info.main }} />;
      case 'pdf-count': return <PdfIcon sx={{ ...iconStyle, color: theme.palette.primary.main }} />;
      case 'check-file': return <PdfIcon sx={{ ...iconStyle, color: theme.palette.warning.main }} />;
      case 'download-pdf': return <DownloadIcon sx={{ ...iconStyle, color: theme.palette.warning.dark }} />;
      case 'query-ocr-results': return <AnalyticsIcon sx={{ ...iconStyle, color: theme.palette.secondary.main }} />;
      case 'view-table': return <TableIcon sx={{ ...iconStyle, color: theme.palette.grey[600] }} />;
      case 'navego a Dashboard': return <DashboardIcon sx={{ ...iconStyle, color: theme.palette.primary.dark }} />;
      case 'navego a KPI': return <AnalyticsIcon sx={{ ...iconStyle, color: theme.palette.info.dark }} />;
      case 'update-ocr': return <AnalyticsIcon sx={{ ...iconStyle, color: theme.palette.success.dark }} />;
      case 'view-demo-fields': return <TableIcon sx={{ ...iconStyle, color: theme.palette.info.main }} />;
      case 'update-table': return <TableIcon sx={{ ...iconStyle, color: theme.palette.success.main }} />;
      case 'geology_wells_viewed':
      case 'Ingreso a gestión de geología':
      case 'Consulta rangos de geología':
      case 'Genera análisis de geología':
        return <TerrainIcon sx={{ ...iconStyle, color: getActionColor(action) }} />;
      default: return <ScheduleIcon sx={{ ...iconStyle, color: theme.palette.grey[500] }} />;
    }
  };

  const getActionColor = (action) => {
    if (isGeologyAction(action)) {
      return '#8d6e63';
    }
    switch (action) {
      case 'login': return theme.palette.success.main;
      case 'logout': return theme.palette.error.main;
      case 'upload': return theme.palette.info.main;
      case 'pdf-count': return theme.palette.primary.main;
      case 'check-file': return theme.palette.warning.main;
      case 'download-pdf': return theme.palette.warning.dark;
      case 'query-ocr-results': return theme.palette.secondary.main;
      case 'view-table': return theme.palette.grey[600];
      case 'navego a Dashboard': return theme.palette.primary.dark;
      case 'navego a KPI': return theme.palette.info.dark;
      case 'update-ocr': return theme.palette.success.dark;
      case 'view-demo-fields': return theme.palette.info.main;
      case 'update-table': return theme.palette.success.main;
      case 'geology_wells_viewed':
      case 'Ingreso a gestión de geología':
      case 'Consulta rangos de geología':
      case 'Genera análisis de geología':
        return '#8d6e63';
      default: return theme.palette.primary.main;
    }
  };

  const getUserInitials = (userName) => {
    return userName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return t('adminPanel.never');
    const now = new Date();
    const diffMs = now - new Date(timestamp);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t('adminPanel.now');
    if (diffMins < 60) return t('adminPanel.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('adminPanel.hoursAgo', { count: diffHours });
    return t('adminPanel.daysAgo', { count: diffDays });
  };

  const handleUserClick = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };


  const renderTabContent = () => {
    switch (activeTab) {
      case 0:
        return (
          <TraceabilityTab
            logs={logs}
            loading={loading}
            expandedUser={expandedUser}
            handleUserClick={handleUserClick}
            getActionIcon={getActionIcon}
            getActionColor={getActionColor}
            getUserInitials={getUserInitials}
            getTimeAgo={getTimeAgo}
            theme={theme}
            translateAction={translateAction}
            translateActivityInfo={translateActivityInfo}
          />
        );
      case 1:
        return <MembersTab />;
      case 2:
        return <GeneralTab />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{
      p: { xs: 1, md: 2 },
      background: `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
      minHeight: '100vh',
      backgroundImage: 'radial-gradient(circle at 15% 50%, rgba(120, 119, 198, 0.03) 0%, transparent 65%), radial-gradient(circle at 85% 30%, rgba(255, 119, 198, 0.03) 0%, transparent 55%)'
    }}>
      <Card sx={{
        mb: 4,
        bgcolor: "#0f0f47",

        color: 'white',
        borderRadius: 3,
        boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
        }
      }}>
        <CardContent sx={{ p: { xs: 3, md: 4 }, position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
              <Avatar sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                width: { xs: 70, md: 80 },
                height: { xs: 70, md: 80 },
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
              }}>
                <SecurityIcon sx={{ fontSize: { xs: 32, md: 40 } }} />
              </Avatar>
              <Box>
                <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
                  {t('adminPanel.panelTitle')}
                </Typography>
                <Typography variant="h6" sx={{
                  opacity: 0.9,
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  fontWeight: 400
                }}>
                  {t('adminPanel.panelSubtitle')}
                </Typography>
              </Box>
            </Box>
            <Tooltip title={t('adminPanel.refresh')}>
              <IconButton
                onClick={fetchLogs}
                size="large"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  backdropFilter: 'blur(10px)',
                  width: 56,
                  height: 56,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                    transform: 'rotate(180deg)',
                    transition: 'all 0.3s ease'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 4 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MetricCard
            value={stats.totalUsers}
            label={t('adminPanel.totalUsers')}
            icon={GroupsIcon}
            sx={{
              backgroundColor: '#0f0f47',
              color: '#fff'
            }}
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MetricCard
            value={stats.totalActivities}
            label={t('adminPanel.totalActivities')}
            icon={BarChartIcon}
            sx={{
              backgroundColor: '#0f0f47',
              color: '#fff'
            }}
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MetricCard
            value={stats.activeToday}
            label={t('adminPanel.activeToday')}
            icon={EventAvailableIcon}
            sx={{
              backgroundColor: '#0f0f47',
              color: '#fff'
            }}
            loading={loading}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <MetricCard
            value={stats.activeThisWeek}
            label={t('adminPanel.activeThisWeek')}
            icon={TrendingUpIcon}
            sx={{
              backgroundColor: '#0f0f47',
              color: '#fff'
            }}
            loading={loading}
          />
        </Box>
      </Stack>

      <Card sx={{
        mb: 4,
        borderRadius: 3,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              py: 3,
              fontSize: '1rem',
              fontWeight: 600,
              minHeight: 'auto',
              textTransform: 'none',
              color: 'text.secondary',
              transition: 'all 0.3s ease',
              '&:hover': {
                color: 'primary.main',
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              }
            },
            '& .Mui-selected': {
              color: 'primary.main',
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.primary.main,
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab
            icon={<TimelineIcon />}
            iconPosition="start"
            label={t('adminPanel.traceability')}
          />
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label={t('adminPanel.members')}
          />
          <Tab
            icon={<DashboardIcon />}
            iconPosition="start"
            label={t('adminPanel.general')}
          />
        </Tabs>
      </Card>

      {loading ?
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Stack alignItems="center" spacing={3}>
            <CircularProgress size={80} thickness={2} />
            <Typography variant="h6" color="textSecondary">
              {t('adminPanel.loading')}
            </Typography>
          </Stack>
        </Box>
        :
        <Fade in timeout={500}>
          <Box>
            {renderTabContent()}
          </Box>
        </Fade>
      }
    </Box>
  );
}